const PROFILE_CONTINUITY_KEY = "morfeusz:profile-continuity:v2";
const LEGACY_PROFILE_CONTINUITY_KEY = "morfeusz:profile-continuity:v1";
const PROFILE_CONTINUITY_EVENT = "morfeusz:profile-continuity";

const LEGACY_PROFILE_BUILD_OPTIN_KEY = "morfeusz:profile:build_optin:v3";
const LEGACY_PROFILE_AI_USE_OPTIN_KEY = "morfeusz:profile:ai_use_optin:v3";
const LEGACY_PROFILE_PACK_KEY = "morfeusz:profile:e2e_pack:v3";
const LEGACY_PROFILE_VERSION_KEY = "morfeusz:profile:version:v3";
const LEGACY_PROFILE_UPDATED_AT_KEY = "morfeusz:profile:updated_at:v3";
const LEGACY_PROFILE_LAST_REBUILT_AT_KEY = "morfeusz:profile:last_rebuilt_at:v3";

export const PROFILE_ANALYSIS_SOURCE_BACKEND_4E = "backend_4e";
export const PROFILE_ANALYSIS_SOURCE_LOCAL_MOCK = "local_mock";

export const PROFILE_CONTEXT_DISABLED = Object.freeze({
  enabled: false,
  reason: "disabled",
  buildOptin: false,
  aiUseOptin: false,
  hasPack: false,
});

const emptyStatus = {
  version: 2,
  explicitConsent: false,
  buildOptin: false,
  aiUseOptin: false,
  hasPack: false,
  updatedAt: "",
  lastRebuiltAt: "",
  profile: null,
};

const isBrowser = () => typeof window !== "undefined" && Boolean(window.localStorage);

const safeParse = (raw) => {
  if (!raw || typeof raw !== "string") return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (_) {
    return null;
  }
};

const safeString = (value) => (typeof value === "string" ? value : "");

const readLegacyPack = () => {
  if (!isBrowser()) return null;
  return safeParse(window.localStorage.getItem(LEGACY_PROFILE_PACK_KEY));
};

const normalizeStatus = (raw) => {
  if (!raw || typeof raw !== "object") return { ...emptyStatus };

  const explicitConsent = raw.version === 2 && raw.explicitConsent === true;
  const buildOptin = explicitConsent && raw.buildOptin === true;
  const aiUseOptin = buildOptin && raw.aiUseOptin === true;
  const profile = raw.profile && typeof raw.profile === "object" ? raw.profile : null;
  const legacyPack = readLegacyPack();
  const pack = profile || (legacyPack && typeof legacyPack === "object" ? legacyPack : null);

  return {
    version: 2,
    explicitConsent,
    buildOptin,
    aiUseOptin,
    hasPack: buildOptin && Boolean(raw.hasPack || pack),
    updatedAt: safeString(raw.updatedAt),
    lastRebuiltAt: safeString(raw.lastRebuiltAt || raw.updatedAt),
    profile: pack,
  };
};

const readRawStatus = () => {
  if (!isBrowser()) return null;
  const current = safeParse(window.localStorage.getItem(PROFILE_CONTINUITY_KEY));
  if (current) return current;

  const legacy = safeParse(window.localStorage.getItem(LEGACY_PROFILE_CONTINUITY_KEY));
  const legacyPack = readLegacyPack();
  if (!legacy && !legacyPack) return null;

  return {
    version: 2,
    explicitConsent: false,
    buildOptin: false,
    aiUseOptin: false,
    hasPack: Boolean(legacy?.hasPack || legacy?.profile || legacyPack),
    updatedAt: safeString(legacy?.updatedAt || window.localStorage.getItem(LEGACY_PROFILE_UPDATED_AT_KEY)),
    lastRebuiltAt: safeString(legacy?.lastRebuiltAt || window.localStorage.getItem(LEGACY_PROFILE_LAST_REBUILT_AT_KEY)),
    profile: legacy?.profile && typeof legacy.profile === "object" ? legacy.profile : legacyPack,
  };
};

const writeStatus = (next) => {
  if (!isBrowser()) return;
  const normalized = normalizeStatus(next);
  window.localStorage.setItem(PROFILE_CONTINUITY_KEY, JSON.stringify(normalized));
  try {
    window.dispatchEvent(new CustomEvent(PROFILE_CONTINUITY_EVENT, { detail: normalized }));
  } catch (_) {
    window.dispatchEvent(new Event(PROFILE_CONTINUITY_EVENT));
  }
};

export const getProfileContinuityStatus = () => normalizeStatus(readRawStatus());

export const setProfileContinuityStatus = (patch = {}) => {
  const current = getProfileContinuityStatus();
  const changesConsent = Object.prototype.hasOwnProperty.call(patch, "buildOptin") || Object.prototype.hasOwnProperty.call(patch, "aiUseOptin");
  const buildOptin = Object.prototype.hasOwnProperty.call(patch, "buildOptin") ? patch.buildOptin === true : current.buildOptin;
  const aiUseOptin = buildOptin && (Object.prototype.hasOwnProperty.call(patch, "aiUseOptin") ? patch.aiUseOptin === true : current.aiUseOptin);
  const profile = patch.profile !== undefined ? patch.profile : current.profile;

  const next = {
    ...current,
    ...patch,
    version: 2,
    explicitConsent: changesConsent ? true : current.explicitConsent === true,
    buildOptin,
    aiUseOptin,
    profile: profile && typeof profile === "object" ? profile : null,
    hasPack: buildOptin && Boolean(patch.hasPack ?? current.hasPack ?? profile),
    updatedAt: new Date().toISOString(),
    lastRebuiltAt: patch.lastRebuiltAt || current.lastRebuiltAt || current.updatedAt || "",
  };

  writeStatus(next);
  return getProfileContinuityStatus();
};

export const readProfileConsent = () => {
  const status = getProfileContinuityStatus();
  return {
    buildOptin: status.buildOptin,
    aiUseOptin: status.aiUseOptin,
  };
};

export const writeProfileConsent = ({ buildOptin, aiUseOptin }) => {
  setProfileContinuityStatus({ buildOptin: Boolean(buildOptin), aiUseOptin: Boolean(aiUseOptin) });
  return true;
};

export const readEncryptedProfilePack = () => {
  const status = getProfileContinuityStatus();
  return status.profile || null;
};

export const writeEncryptedProfilePack = (pack) => {
  const status = getProfileContinuityStatus();
  if (!status.buildOptin) return false;
  const now = new Date().toISOString();
  setProfileContinuityStatus({
    profile: pack && typeof pack === "object" ? pack : null,
    hasPack: Boolean(pack),
    updatedAt: now,
    lastRebuiltAt: now,
  });
  return true;
};

export const purgeProfileContinuity = () => {
  if (!isBrowser()) return false;
  window.localStorage.removeItem(PROFILE_CONTINUITY_KEY);
  window.localStorage.removeItem(LEGACY_PROFILE_CONTINUITY_KEY);
  window.localStorage.removeItem(LEGACY_PROFILE_PACK_KEY);
  window.localStorage.removeItem(LEGACY_PROFILE_VERSION_KEY);
  window.localStorage.removeItem(LEGACY_PROFILE_UPDATED_AT_KEY);
  window.localStorage.removeItem(LEGACY_PROFILE_LAST_REBUILT_AT_KEY);
  window.localStorage.setItem(LEGACY_PROFILE_BUILD_OPTIN_KEY, "0");
  window.localStorage.setItem(LEGACY_PROFILE_AI_USE_OPTIN_KEY, "0");
  writeStatus({ ...emptyStatus, updatedAt: new Date().toISOString() });
  return true;
};

export const updateProfileContinuityProfile = (profile) => {
  const current = getProfileContinuityStatus();
  if (!current.buildOptin) return current;
  return setProfileContinuityStatus({
    profile: profile && typeof profile === "object" ? profile : current.profile,
    hasPack: Boolean(profile || current.profile),
  });
};

export const clearProfileContinuityStatus = () => {
  purgeProfileContinuity();
  return getProfileContinuityStatus();
};

export const getProfileContextGate = () => {
  const status = getProfileContinuityStatus();

  if (!status.buildOptin) {
    return { ...PROFILE_CONTEXT_DISABLED, reason: "build_optin_disabled" };
  }

  if (!status.aiUseOptin) {
    return {
      ...PROFILE_CONTEXT_DISABLED,
      reason: "ai_use_optin_disabled",
      buildOptin: true,
      aiUseOptin: false,
      hasPack: status.hasPack,
    };
  }

  if (!status.hasPack) {
    return {
      ...PROFILE_CONTEXT_DISABLED,
      reason: "profile_pack_missing",
      buildOptin: true,
      aiUseOptin: true,
      hasPack: false,
    };
  }

  return {
    enabled: true,
    reason: "ready_requires_vault_unlock",
    buildOptin: true,
    aiUseOptin: true,
    hasPack: true,
    version: status.version,
    updatedAt: status.updatedAt,
    lastRebuiltAt: status.lastRebuiltAt,
  };
};

export const buildProfileContextForAnalysis = async () => {
  const gate = getProfileContextGate();
  if (!gate.enabled) return gate;

  const session = window.MDVaultSession;
  if (!session || typeof session.ensureUnlocked !== "function") {
    return {
      ...PROFILE_CONTEXT_DISABLED,
      reason: "vault_session_missing",
      buildOptin: true,
      aiUseOptin: true,
      hasPack: true,
    };
  }

  return {
    ...PROFILE_CONTEXT_DISABLED,
    reason: "decrypt_not_implemented",
    buildOptin: true,
    aiUseOptin: true,
    hasPack: true,
  };
};

export const getProfileExtractEligibility = ({ dream, analysis } = {}) => {
  const consent = readProfileConsent();

  if (!consent.buildOptin) {
    return {
      ok: false,
      reason: "build_optin_disabled",
      buildOptin: false,
      analysisSource: analysis?.source || "none",
    };
  }

  if (!dream?.id) {
    return {
      ok: false,
      reason: "dream_missing",
      buildOptin: true,
      analysisSource: analysis?.source || "none",
    };
  }

  if (!analysis?.html && !analysis?.plainText) {
    return {
      ok: false,
      reason: "analysis_missing",
      buildOptin: true,
      dreamId: dream.id,
      analysisSource: analysis?.source || "none",
    };
  }

  if (analysis.source !== PROFILE_ANALYSIS_SOURCE_BACKEND_4E) {
    return {
      ok: false,
      reason: "analysis_not_backend_4e",
      buildOptin: true,
      dreamId: dream.id,
      analysisSource: analysis.source || "unknown",
    };
  }

  return {
    ok: true,
    reason: "eligible",
    buildOptin: true,
    dreamId: dream.id,
    analysisSource: analysis.source,
    analysisMode: analysis.mode || "ai",
    analysisCreatedAt: analysis.createdAt || "",
  };
};

export const PROFILE_CONTINUITY_KEYS = Object.freeze({
  PROFILE_CONTINUITY_KEY,
  LEGACY_PROFILE_CONTINUITY_KEY,
  PROFILE_BUILD_OPTIN_KEY: LEGACY_PROFILE_BUILD_OPTIN_KEY,
  PROFILE_AI_USE_OPTIN_KEY: LEGACY_PROFILE_AI_USE_OPTIN_KEY,
  PROFILE_PACK_KEY: LEGACY_PROFILE_PACK_KEY,
  PROFILE_VERSION_KEY: LEGACY_PROFILE_VERSION_KEY,
  PROFILE_UPDATED_AT_KEY: LEGACY_PROFILE_UPDATED_AT_KEY,
  PROFILE_LAST_REBUILT_AT_KEY: LEGACY_PROFILE_LAST_REBUILT_AT_KEY,
});

export const PROFILE_CONTINUITY_STORAGE_KEY = PROFILE_CONTINUITY_KEY;
export const PROFILE_CONTINUITY_CHANGE_EVENT = PROFILE_CONTINUITY_EVENT;
